import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from '../../../components/ui/Icons';
import { useTheme, type ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { mentionBadgeClassName, type MentionBadgeTone } from '../utils/mentionUtils';

interface CollaborationGuideInfoProps {
  className?: string;
  panelZIndex?: number;
}

interface GuideRow {
  token: string;
  meaning: string;
  tone: MentionBadgeTone;
}

const PANEL_WIDTH = 352;
const VIEWPORT_PADDING = 8;
const PANEL_GAP = 6;

function computeGuidePanelStyle(anchor: DOMRect): CSSProperties {
  const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);
  let left = anchor.left;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - VIEWPORT_PADDING - width;
  }
  left = Math.max(VIEWPORT_PADDING, left);

  const spaceBelow = window.innerHeight - anchor.bottom - PANEL_GAP - VIEWPORT_PADDING;
  const spaceAbove = anchor.top - PANEL_GAP - VIEWPORT_PADDING;
  const placeAbove = spaceBelow < 260 && spaceAbove > spaceBelow;

  if (placeAbove) {
    return {
      position: 'fixed',
      left,
      width,
      top: anchor.top - PANEL_GAP,
      transform: 'translateY(-100%)',
      maxHeight: Math.min(420, Math.max(160, spaceAbove)),
      overflowY: 'auto',
    };
  }

  return {
    position: 'fixed',
    left,
    width,
    top: anchor.bottom + PANEL_GAP,
    maxHeight: Math.min(420, Math.max(160, spaceBelow)),
    overflowY: 'auto',
  };
}

const CollaborationGuideInfo: React.FC<CollaborationGuideInfoProps> = ({
  className,
  panelZIndex = Z_INDEX.modalHigh,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('emails');

  const rows: GuideRow[] = [
    { token: t('collaborationGuideRowPersonToken'), meaning: t('collaborationGuideRowPersonMeaning'), tone: 'person' },
    { token: t('collaborationGuideRowAssigneeToken'), meaning: t('collaborationGuideRowAssigneeMeaning'), tone: 'assignee' },
    { token: t('collaborationGuideRowTeammatesToken'), meaning: t('collaborationGuideRowTeammatesMeaning'), tone: 'teammates' },
    { token: t('collaborationGuideRowAllToken'), meaning: t('collaborationGuideRowAllMeaning'), tone: 'all' },
    { token: t('collaborationGuideRowHereToken'), meaning: t('collaborationGuideRowHereMeaning'), tone: 'here' },
    { token: t('collaborationGuideRowInternalToken'), meaning: t('collaborationGuideRowInternalMeaning'), tone: 'internal' },
    { token: t('collaborationGuideRowEditToken'), meaning: t('collaborationGuideRowEditMeaning'), tone: 'edit' },
  ];

  return (
    <CollaborationGuideTooltip
      colors={colors}
      rows={rows}
      className={className}
      panelZIndex={panelZIndex}
    />
  );
};

function CollaborationGuideTooltip({
  colors,
  rows,
  className,
  panelZIndex,
}: {
  colors: ThemeColors;
  rows: GuideRow[];
  className?: string;
  panelZIndex: number;
}) {
  const { t } = useTranslation('emails');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    setPanelStyle({ ...computeGuidePanelStyle(el.getBoundingClientRect()), ...zStyle(panelZIndex) });
  }, [panelZIndex]);

  const showPanel = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleHide = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const panelContent =
    open && panelStyle ? (
      <div
        ref={panelRef}
        id="collaboration-guide-tooltip"
        role="tooltip"
        style={panelStyle}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
      >
        <div
          className={cn(
            'rounded-lg border p-3.5 shadow-lg ring-1 ring-indigo-500/10 dark:ring-indigo-400/10',
            colors.surface,
            colors.border,
          )}
        >
          <p className={cn('text-sm font-semibold mb-2.5 text-indigo-700 dark:text-indigo-300')}>
            {t('collaborationGuideTitle')}
          </p>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wide pb-2 pr-3 w-32',
                    colors.textMuted,
                  )}
                >
                  {t('collaborationGuideColMention')}
                </th>
                <th className={cn('text-xs font-semibold uppercase tracking-wide pb-2', colors.textMuted)}>
                  {t('collaborationGuideColMeaning')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.token} className="align-top">
                  <td className="py-2 pr-3">
                    <span
                      className={cn(
                        'inline-block text-sm font-semibold px-2 py-0.5 rounded',
                        mentionBadgeClassName(row.tone),
                      )}
                    >
                      {row.token}
                    </span>
                  </td>
                  <td className={cn('py-2 text-sm leading-snug', colors.textSecondary)}>{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'inline-flex items-center justify-center rounded-full p-1 transition-colors',
          open
            ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-950/40'
            : cn(colors.textMuted, colors.interactiveHover),
        )}
        aria-label={t('collaborationGuideAria')}
        aria-describedby={open ? 'collaboration-guide-tooltip' : undefined}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
        onFocus={showPanel}
        onBlur={(event) => {
          const next = event.relatedTarget as Node | null;
          if (next && panelRef.current?.contains(next)) return;
          setOpen(false);
        }}
      >
        <InfoIcon className="w-4 h-4" />
      </button>
      {typeof document !== 'undefined' && panelContent ? createPortal(panelContent, document.body) : null}
    </div>
  );
}

export default CollaborationGuideInfo;
